"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { SitePromoModal } from "@/components/site/SitePromoModal";
import type { SitePromoRecord } from "@/server/modules/site-promos/shared";
import {
  decidePromoSession,
  parseImpressionAt,
  PROMO_LAST_ID_KEY,
  PROMO_LAST_SHOWN_KEY,
} from "@/lib/promo-frequency";

/**
 * The single place a promotional modal can be opened automatically.
 *
 * Mounted by the homepage only, so an inner route cannot render this at all —
 * the route rule is architectural, not a CSS or early-return afterthought.
 * The pathname check below is a second belt for client-side navigation away
 * from "/" while this component is still mounted.
 *
 * One SESSION per 24 hours: the first campaign opens after its own delay and
 * a screen's worth of scrolling, each next one opens as soon as the previous is
 * closed, and then nothing at all for 24 hours. The impression is recorded once, when the first modal appears, so
 * the whole run costs the visitor a single 24-hour window.
 *
 * Frequency lives in lib/promo-frequency.ts as pure functions; this component
 * only supplies the browser bits (storage reads, a timer) and records the
 * impression. See HANDOFF.md → "Promotional modal rule".
 */

/** localStorage can throw (Safari private mode, blocked third-party contexts).
 *  A module-level fallback keeps the one-per-session guarantee even then —
 *  worse than a real 24h cooldown, but it still prevents both promos opening
 *  during the same page session, and it never throws. */
const memoryStore: { lastShownAt: number | null; lastPromoKey: string | null } = {
  lastShownAt: null,
  lastPromoKey: null,
};

function readPromoState(): { lastShownAt: number | null; lastPromoKey: string | null } {
  try {
    const stored = parseImpressionAt(window.localStorage.getItem(PROMO_LAST_SHOWN_KEY));
    if (stored != null) {
      return { lastShownAt: stored, lastPromoKey: window.localStorage.getItem(PROMO_LAST_ID_KEY) };
    }
    // No usable stored value: fall through to whatever this session recorded,
    // so a browser that silently drops writes still can't show two promos.
    return { ...memoryStore };
  } catch {
    return { ...memoryStore };
  }
}

function writePromoImpression(key: string, at: number) {
  memoryStore.lastShownAt = at;
  memoryStore.lastPromoKey = key;
  try {
    window.localStorage.setItem(PROMO_LAST_SHOWN_KEY, String(at));
    window.localStorage.setItem(PROMO_LAST_ID_KEY, key);
  } catch {
    /* in-memory fallback above already holds it for this session */
  }
}

export function HomePromoController({ promos }: { promos: SitePromoRecord[] }) {
  const pathname = usePathname();
  /** The session's campaigns, in order, plus how far through it we are. */
  const [session, setSession] = useState<SitePromoRecord[]>([]);
  const [step, setStep] = useState(0);
  const active = session[step] ?? null;

  /** Guards the IMPRESSION, not the arming.
   *
   *  An earlier version set this when the timer was armed, which quietly broke
   *  client-side navigation: React tears the effect down and re-runs it during
   *  a transition, the cleanup cleared the pending timer, and the already-set
   *  flag then refused to arm a new one — so arriving at the homepage from an
   *  inner page showed nothing at all. Re-arming is harmless and idempotent
   *  (cleanup clears the old timer); firing twice is what must not happen, and
   *  that is what this now prevents, including under React Strict Mode. */
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;

    // Everything below runs in an effect, never during render: it reads
    // localStorage, which does not exist on the server. The modal therefore
    // starts closed on both server and client and only opens once eligibility
    // has been evaluated — no hydration mismatch, and no flash of a modal that
    // then disappears.
    const { lastShownAt } = readPromoState();
    const decision = decidePromoSession({
      pathname,
      promos,
      lastShownAt,
      now: Date.now(),
    });
    if (!decision.show) return;

    // Opens only once BOTH the campaign's delay has passed AND the visitor has
    // scrolled a screen's height into the page. On a phone the panel covers
    // the whole first screen, so opening on a timer alone blocked people who
    // had not even started reading (site audit, 28 Sept 2026). Someone who
    // never scrolls is never interrupted.
    let delayDone = false;
    let scrolledEnough = false;
    const open = () => {
      if (shown.current || !delayDone || !scrolledEnough) return;
      shown.current = true;
      // Recorded ONCE, when the first modal actually appears — not per campaign
      // and not on CTA click. Dismissing, closing, pressing Escape and clicking
      // through are all outcomes of an interruption they already saw, and the
      // rest of the session rides on this same impression.
      writePromoImpression(decision.promos[0].key, Date.now());
      setSession(decision.promos);
      setStep(0);
    };
    const onScroll = () => {
      if (window.scrollY < window.innerHeight) return;
      scrolledEnough = true;
      window.removeEventListener("scroll", onScroll);
      open();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const timer = window.setTimeout(() => {
      delayDone = true;
      open();
    }, decision.firstDelayMs);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
    // `promos` comes from a server component and is stable for this page view,
    // so pathname is what genuinely re-decides. Leaving `promos` out also stops
    // a new array identity from restarting the delay on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Closing advances the session. The next campaign opens with no further
  // delay — the visitor is plainly present, having just dismissed a panel, so
  // making them wait again reads as the handover being broken. Once the session
  // is exhausted `active` is undefined and nothing renders until the cooldown
  // expires.
  const advance = useCallback(() => setStep((current) => current + 1), []);

  return <SitePromoModal promo={active} onClose={advance} />;
}
